import { router } from '@inertiajs/react'
import { useId, useState } from 'react'
import { Search } from 'lucide-react'

interface BlogSearchFormProps {
  /** Pre-filled query (e.g. on the search results page). */
  initialQuery?: string
  autoFocus?: boolean
  /** 'lg' for the search-page hero, 'md' for the sidebar. */
  size?: 'md' | 'lg'
}

/** Blog article search — submits to /blogs/search. Shared by the blog
 *  sidebar and the search results page hero. */
export default function BlogSearchForm({
  initialQuery = '',
  autoFocus = false,
  size = 'md'
}: BlogSearchFormProps) {
  const [query, setQuery] = useState(initialQuery)
  const inputId = useId()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    router.get('/blogs/search', { q: trimmed })
  }

  const isLg = size === 'lg'

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      aria-label="Search blog articles"
      className={isLg ? 'mx-auto w-full max-w-xl' : 'w-full'}
    >
      <label htmlFor={inputId} className="sr-only">
        Search articles
      </label>
      <div className="relative">
        <Search
          size={isLg ? 18 : 15}
          aria-hidden="true"
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
        />
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles…"
          autoFocus={autoFocus}
          enterKeyHint="search"
          className={`w-full rounded-full border border-white/10 bg-white/[0.04] text-white placeholder:text-white/35 transition-all focus:border-[#ff6a1a]/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-[#ff6a1a]/20 ${
            isLg ? 'h-12 pl-11 pr-5 text-base' : 'h-10 pl-10 pr-4 text-sm'
          }`}
        />
      </div>
    </form>
  )
}
